import type { ChipsStateMap } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/types";
import {
  SuggestionType,
  UserJourney,
} from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/types";
import i18n from "@mirage/translations";

const getDynamicChips = (i: number) => {
  return Array.from({ length: i }).map(
    () => ({ type: SuggestionType.DYNAMIC, enum: "dynamic" }) as const,
  );
};

// Map business logic to appropriate suggestions based on FSS context
export const SUGGESTION_CHIPS_STATE_MAP: ChipsStateMap = {
  [UserJourney.FTUX]: {
    "fss-unscoped": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t(
            "assistant_empty_chat_promotion_static_video_query_suggestion_copy",
          ),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_video_in_dash",
      },
      ...getDynamicChips(1),
    ],
    "fss-file": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t(
            "assistant_empty_chat_promotion_static_video_query_suggestion_copy",
          ),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_video_in_dash",
      },
      { type: SuggestionType.FSS_WRITE, enum: "status_report" },
    ],
    "fss-folder": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.CONTEXTUAL,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_summarize_folder"),
        enum: "summarize_this_folder",
      },
      ...getDynamicChips(1),
    ],
    "fss-file-preview": {
      videoFiles: [
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_suggestion"),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_dash",
        },
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t(
              "assistant_empty_chat_promotion_static_video_query_suggestion_copy",
            ),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_video_in_dash",
        },
        {
          type: SuggestionType.CONTEXTUAL,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_summarize_video"),
          enum: "summarize_this_video",
        },
      ],
      audioFiles: [
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_suggestion"),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_dash",
        },
        ...getDynamicChips(2),
      ],
      imageFiles: [
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_suggestion"),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_dash",
        },
        ...getDynamicChips(2),
      ],
      documentsWithBody: [
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t(
              "assistant_empty_chat_promotion_static_document_query_suggestion_copy",
            ),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_documents_in_dash",
        },
        {
          type: SuggestionType.CONTEXTUAL,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_summarize_document"),
          enum: "summarize_this_document",
        },
        ...getDynamicChips(1),
      ],
      other: [
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_suggestion"),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_dash",
        },
        {
          type: SuggestionType.CONTEXTUAL,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_summarize_file"),
          enum: "summarize_this_file",
        },
        ...getDynamicChips(1),
      ],
    },
    "fss-files": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.CONTEXTUAL,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_summarize_files"),
        enum: "summarize_these_files",
      },
      { type: SuggestionType.DYNAMIC, enum: "dynamic" },
    ],
    "fss-stack": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
    ],
  },
  [UserJourney.ATUX]: {
    "fss-unscoped": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t(
            "assistant_empty_chat_promotion_static_video_query_suggestion_copy",
          ),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_video_in_dash",
      },
      { type: SuggestionType.FSS_WRITE, enum: "status_report" },
    ],
    "fss-file": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t(
            "assistant_empty_chat_promotion_static_video_query_suggestion_copy",
          ),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_video_in_dash",
      },
      { type: SuggestionType.FSS_WRITE, enum: "status_report" },
    ],
    "fss-folder": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
      {
        type: SuggestionType.CONTEXTUAL,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_summarize_folder"),
        enum: "summarize_this_folder",
      },
      ...getDynamicChips(1),
    ],
    "fss-file-preview": {
      videoFiles: [
        {
          type: SuggestionType.CONTEXTUAL,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_summarize_video"),
          enum: "summarize_this_video",
        },
        ...getDynamicChips(2),
      ],
      audioFiles: getDynamicChips(3),
      imageFiles: getDynamicChips(3),
      documentsWithBody: [
        {
          type: SuggestionType.CONTEXTUAL,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_summarize_document"),
          enum: "summarize_this_document",
        },
        ...getDynamicChips(2),
      ],
      other: [
        {
          type: SuggestionType.STATIC,
          getSuggestionText: () =>
            i18n.t("assistant_empty_chat_promotion_suggestion"),
          hasPrescribedAnswer: true,
          enum: "what_can_i_do_with_dash",
        },
        ...getDynamicChips(2),
      ],
    },
    "fss-files": [
      {
        type: SuggestionType.CONTEXTUAL,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_summarize_files"),
        enum: "summarize_these_files",
      },
      ...getDynamicChips(2),
    ],
    "fss-stack": [
      {
        type: SuggestionType.STATIC,
        getSuggestionText: () =>
          i18n.t("assistant_empty_chat_promotion_suggestion"),
        hasPrescribedAnswer: true,
        enum: "what_can_i_do_with_dash",
      },
    ],
  },
};
